import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { getGroupByFromTableViewRequest } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { getSecondaryBreakdownFromTableViewRequest } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { TableViewComponentProps } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { TableViewCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TableViewDefinition';
import { TableViewOutput } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { TableViewRequest } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getListValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getStringValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { ExplorePageFailure } from 'apps/sigma2-ui/src/explore-section/components/ExplorePageFailure';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { TableGrid } from 'apps/sigma2-ui/src/explore-section/components/TableGrid';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';
import { DEFAULT_VIEW_MODE } from 'apps/sigma2-ui/src/explore-section/components/TableViewConfig';
import { DefaultOverallDisplayMode } from 'libs/end-to-end/src/view-configurations';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconLookupDataSvg } from 'apps/sigma2-ui/src/components/svg/IconLookupData';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import cn from 'classnames';
import { getSelectedSecondaryBreakdownFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { viewModeOptions } from 'apps/sigma2-ui/src/explore-section/components/TableViewConfig';
import { useFormatCellValue } from 'apps/sigma2-ui/src/explore-section/hooks/useFormatCellValue';
import { useCompatibleTableData } from 'apps/sigma2-ui/src/explore-section/hooks/useCompatibleTableData';
import { ViewModeSelector } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/ViewModeSelector';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { SELECTED_SECONDARY_BREAKDOWN_NO_SELECTION_VALUE } from 'libs/end-to-end/src/filters-v2/filter-utils';

// Extended props interface that includes optional chart for database persistence
interface ExtendedTableViewComponentProps extends TableViewComponentProps {
  chart?: DraftDashboardChart; // Database persistence when provided
  setViewConfiguration?: (viewConfiguration: ViewConfigurationSchema) => void;
}

interface DataDisplayPreferencesProps {
  visibleRows: number[];
  displayPreferencesHeader: { key: string; label: string; className: string }[];
  displayPreferencesBody: {
    key: string;
    cells: { key: string; content: string; className: string }[];
  }[];
  viewConfiguration: ViewConfigurationSchema;
  setViewConfiguration?: (viewConfiguration: ViewConfigurationSchema) => void;
}

const DataDisplayPreferences = ({
  visibleRows,
  displayPreferencesHeader,
  displayPreferencesBody,
  viewConfiguration,
  setViewConfiguration,
}: DataDisplayPreferencesProps) => {
  const language = useLanguage();

  const handleSelectCell = useCallback(
    (index: number, selected: boolean) => {
      if (!setViewConfiguration || !viewConfiguration) {
        return;
      }

      if (selected) {
        setViewConfiguration({
          ...viewConfiguration,
          visibleDepartments: [
            ...(viewConfiguration.visibleDepartments ?? []),
            index,
          ],
        });
      } else {
        setViewConfiguration({
          ...viewConfiguration,
          visibleDepartments:
            viewConfiguration.visibleDepartments?.filter((k) => k !== index) ??
            [],
        });
      }
    },
    [setViewConfiguration, viewConfiguration],
  );

  return (
    <div className="w-full border-t-1 border-gray-150 border-solid mt-8 relative">
      <div className="flex justify-between mt-4">
        <div className="flex items-center gap-2 ">
          <IconLookupDataSvg />
          <S2Typography
            variant={'headline-7-alt'}
            translatedContent={language.dataDisplayPreferences()}
          />
        </div>
        <LightTooltip
          title={
            <div className="flex flex-col gap-2 font-sans">
              <S2Typography
                variant={'headline-7-alt'}
                translatedContent={language.rowDisplayLimits()}
              />
              <S2Typography
                textColorVariant="gray-900"
                variant={'body-7'}
                translatedContent={language.tableViewLimitsDescription()}
              />
            </div>
          }
        >
          <div className="flex items-center gap-2 ">
            <IconInformationSvg
              size={19}
              svg={{ className: 'text-gray-500' }}
            />
            <S2Typography
              variant={'body-7'}
              translatedContent={language.currentlyDisplaying()}
            />
            <S2Typography
              variant={'body-7'}
              textColorVariant="gray-900"
              translatedContent={language.rowsCount({
                departmentsCount: visibleRows.length,
              })}
            />
          </div>
        </LightTooltip>
      </div>

      <div className="pt-7 my-4">
        <TableGrid
          backgroundColorClass={'bg-white'}
          tableClassName={'border-1 border-gray-150 border-solid '}
          searchClassName={
            '!p-0 [&_input]:rounded-none [&_input]:border-transparent'
          }
          hideSearch={false}
          shouldScroll={true}
          header={displayPreferencesHeader}
          body={displayPreferencesBody}
          selectedCells={visibleRows}
          handleSelectCell={handleSelectCell}
        />
      </div>
    </div>
  );
};

export const TableViewComponentPure = (
  props: ExtendedTableViewComponentProps,
) => {
  const {
    data,
    chart,
    setViewConfiguration,
    viewConfiguration,
    selectedGroupBy,
  } = props;

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const compatibleData = useCompatibleTableData({ data, selectedGroupBy });

  const { hideSearch, selectedMetric, baseColors, overallDisplayMode } =
    viewConfiguration;

  const { getColumnDisplayName } = useGetColumnDisplayName();
  const { formatCellValue } = useFormatCellValue();

  const visibleRows = useMemo(() => {
    return viewConfiguration?.visibleDepartments ?? [];
  }, [viewConfiguration]);

  useEffect(() => {
    if (!setViewConfiguration || !viewConfiguration) {
      return;
    }

    if (
      !viewConfiguration?.visibleDepartments ||
      viewConfiguration.visibleDepartments.length === 0
    ) {
      const newVisible =
        compatibleData?.rows.map((_, index) => index).slice(-50) ?? [];

      if (newVisible.length === 0) {
        return;
      }

      setViewConfiguration({
        ...viewConfiguration,
        visibleDepartments: newVisible,
      });
    }
  }, [
    setViewConfiguration,
    compatibleData?.rows,
    viewConfiguration?.visibleDepartments,
    viewConfiguration,
  ]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const displayPreferencesBody = useMemo(() => {
    if (!compatibleData) return [];
    return compatibleData.rows.map((row, rowIndex) => ({
      key: row[0],
      cells: row.map((cell, columnIndex) => ({
        key: `${String(rowIndex)}-${String(columnIndex)}`,
        content:
          columnIndex === 0
            ? cell
            : formatCellValue({
                rawValue: cell,
                header: compatibleData.columnHeaders[columnIndex - 1],
              }),
        className:
          'cursor-default !p-0 border-1 border-gray-150 border-solid justify-center [&>div]:items-center',
      })),
    }));
  }, [compatibleData, formatCellValue]);

  const displayPreferencesHeader = useMemo(() => {
    if (!compatibleData) return [];
    return compatibleData.columnHeaders.map((header) => ({
      key: header,
      label: getColumnDisplayName(header),
      className: 'border-1 border-gray-150 border-solid ',
    }));
  }, [compatibleData, getColumnDisplayName]);

  const viewModeOption = useMemo(() => {
    const viewModeOption = viewModeOptions.find(
      (option) => option.value === viewConfiguration?.defaultViewMode,
    );
    const defaultViewModeOption = viewModeOptions.find(
      (option) => option.value === DEFAULT_VIEW_MODE,
    );
    return viewModeOption ? viewModeOption : defaultViewModeOption;
  }, [viewConfiguration]);

  const ViewModeComponent = useMemo(() => {
    return viewModeOption ? viewModeOption.component : null;
  }, [viewModeOption]);

  const defaultMetric = useMemo(() => {
    if (!compatibleData) {
      return undefined;
    }
    const metricInfo = getMetricAndFocusAreaFromColumnName({
      columnName: compatibleData.columnHeaders[0],
    });
    return metricInfo?.metric.columnHeaderValue;
  }, [compatibleData, getMetricAndFocusAreaFromColumnName]);

  if (!compatibleData) {
    return null;
  }

  return (
    <div
      className={cn('w-full py-3 bg-white', {
        'p-6': chart === undefined,
      })}
      ref={scrollRef}
    >
      {/* View mode selector */}
      <div className="flex items-center gap-2 pb-3.5 bg-white">
        <ViewModeSelector
          viewConfiguration={viewConfiguration}
          setViewConfiguration={setViewConfiguration}
        />

        {viewModeOption?.filters.map((filter) => (
          <filter.component
            key={filter.id}
            data={compatibleData}
            viewConfiguration={viewConfiguration ?? {}}
            setViewConfiguration={setViewConfiguration}
          />
        ))}
      </div>

      {/* Content based on view mode */}
      {ViewModeComponent && (
        <ViewModeComponent
          data={compatibleData}
          visibleRows={visibleRows}
          viewConfiguration={{
            baseColors,
            hideSearch: hideSearch ?? false,
            displayBorder: chart === undefined,
            selectedMetric: selectedMetric ?? defaultMetric,
            colorScheme: 'custom',
            overallDisplayMode: overallDisplayMode ?? DefaultOverallDisplayMode,
          }}
        />
      )}

      {!chart && setViewConfiguration && (
        <DataDisplayPreferences
          visibleRows={visibleRows}
          displayPreferencesHeader={displayPreferencesHeader}
          displayPreferencesBody={displayPreferencesBody}
          viewConfiguration={viewConfiguration}
          setViewConfiguration={setViewConfiguration}
        />
      )}
    </div>
  );
};

export const TableViewStatefulComponent = () => {
  const { myObservation } = useObservation();
  const { selectedOrganizationId } = useUserObservationOrgAndDbIds();

  const { myUser, userAuthenticatedApiCall } = useUser();

  const context = useCustomViz();
  const { doViz, lastSuccessfulCustomVizRequest } = context;

  const [viewConfiguration, setViewConfiguration] =
    useState<ViewConfigurationSchema>({
      defaultViewMode: DEFAULT_VIEW_MODE,
      selectedMetric: undefined,
      visibleDepartments: [],
      baseColors: undefined,
    });

  const shareLinkRawData = useShareLinkSearchParameter();

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): {
      customVizRequest: TableViewRequest | undefined;
      viewConfiguration?: ViewConfigurationSchema;
    } => {
      if (!myObservation || !selectedOrganizationId) {
        return { customVizRequest: undefined };
      }

      let groupBy = getStringValueFromPrimaryFilterValues({
        primaryFilterKey: 'groupBy',
        primaryFilterValues: primaryFilterSelections,
      });

      if (groupBy === SELECTED_SECONDARY_BREAKDOWN_NO_SELECTION_VALUE) {
        groupBy = null;
      }

      const secondaryBreakdown =
        getSelectedSecondaryBreakdownFromPrimaryFilterValues({
          primaryFilterValues: primaryFilterSelections,
        }) || undefined;

      const metrics =
        getListValueFromPrimaryFilterValues({
          primaryFilterKey: 'metrics',
          primaryFilterValues: primaryFilterSelections,
        }) ?? [];

      const { visibleDepartments } = viewConfiguration;

      const visibleDepartmentsString = visibleDepartments?.map((index) =>
        index.toString(),
      );

      if (groupBy && groupBy === secondaryBreakdown) {
        return { customVizRequest: undefined };
      }

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: TableViewRequest = {
        customVizId: 'tableView',
        inputs: {
          extras: {
            groupBy,
            secondaryBreakdown,
            filters,
            metrics,
            visibleDepartments: visibleDepartmentsString,
          },
          dropdowns: {},
        },
        observationLocationType: myObservation.type,
        organizationId: selectedOrganizationId,
      };

      return {
        customVizRequest,
        viewConfiguration,
      };
    },
    [myObservation, selectedOrganizationId, viewConfiguration],
  );

  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      OnApplyFnArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (!customVizRequest || customVizRequest.customVizId !== 'tableView') {
        return;
      }

      doViz(customVizRequest);

      setViewConfiguration((oldConfig) => ({
        ...oldConfig,
        visibleDepartments: [],
      }));
    },
    [doViz, getCustomVizRequest],
  );

  const onApplyTable = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [onAnalyzeWithParams, shareLinkRawData],
  );

  const filterPayload = useFilters({
    feature: 'tableView',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onApplyTable,
    getCustomVizRequest,
    analysisName: undefined,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (!myObservation || !request || request.customVizId !== 'tableView') {
        return;
      }

      const filters = request.inputs.extras.filters;

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'tableView',
          focusArea: null,
          customVizFilters: filters,
          focusAreas: myObservation.focusAreas,
          filterSetDefinitionsWithValues:
            myObservation.filterSetDefinitionsWithValues,
          dimensionDefinitions: myObservation.dimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            myObservation.pipelineAnalysisConfiguration,
        });

      const primaryFilterSelections = getTableViewPrimaryFilterSelections({
        request,
      });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, myObservation, onAnalyzeWithParams],
  );

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: TableViewRequest,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  return (
    <div>
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />
      <Spacing direction="vertical" size="md" className="min-h-96">
        <HorizontalFilterBar
          items={filterPayload.items}
          slotRight={
            <DataRefreshIndicator
              status={context.status}
              resultCreatedAt={context.resultCreatedAt}
            />
          }
        />

        <TableViewCard
          result={context.result}
          status={context.status}
          setViewConfiguration={setViewConfiguration}
          viewConfiguration={viewConfiguration}
          customVizRequest={lastSuccessfulCustomVizRequest ?? undefined}
        />
      </Spacing>
    </div>
  );
};

export const TableViewFullComponent = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const { myObservation } = useObservation();

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  if (
    !myObservation ||
    !selectedDatabaseRevisionId ||
    !selectedDatabaseId ||
    !selectedOrganizationId ||
    !myUser
  ) {
    return <></>;
  }

  return (
    <CustomViz
      observation={myObservation.type}
      organizationId={selectedOrganizationId}
      customVizDefinition={TableViewCustomVizDefinition}
      userAuthenticatedApiCall={userAuthenticatedApiCall}
    >
      <TableViewStatefulComponent />
    </CustomViz>
  );
};

export const getTableViewPrimaryFilterSelections = ({
  request,
}: {
  request: Pick<TableViewRequest, 'inputs'>;
}): PrimaryFilterSelection[] => {
  const extras = request.inputs.extras;

  const { groupBy, secondaryBreakdown, metrics } = extras;

  const primaryFilterSelections: PrimaryFilterSelection[] = [
    {
      primaryFilterKey: 'groupBy',
      value: groupBy || SELECTED_SECONDARY_BREAKDOWN_NO_SELECTION_VALUE,
    },
    {
      primaryFilterKey: 'metrics',
      value: metrics ?? [],
    },
  ];

  if (secondaryBreakdown) {
    primaryFilterSelections.push({
      primaryFilterKey: 'secondaryBreakdown',
      value: secondaryBreakdown,
    });
  }

  return primaryFilterSelections;
};

export const TableViewCard = ({
  result,
  status,
  chart,
  setViewConfiguration,
  viewConfiguration,
  customVizRequest,
}: {
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  chart?: DraftDashboardChart; // Database persistence
  setViewConfiguration?: (viewConfigurations: ViewConfigurationSchema) => void;
  viewConfiguration: ViewConfigurationSchema;
  customVizRequest: CustomVizRequest | undefined;
}) => {
  const { isLoading, data, hasFailed } = useMemo(
    () =>
      parseCustomVizResult({
        result,
        status,
        ResultZodType: TableViewOutput,
      }),
    [result, status],
  );

  const selectedGroupBy = useMemo(() => {
    if (!customVizRequest || customVizRequest.customVizId !== 'tableView') {
      return null;
    }
    return getGroupByFromTableViewRequest({ request: customVizRequest });
  }, [customVizRequest]);

  const selectedSecondaryBreakdown = useMemo(() => {
    if (!customVizRequest || customVizRequest.customVizId !== 'tableView') {
      return undefined;
    }
    return getSecondaryBreakdownFromTableViewRequest({
      request: customVizRequest,
    });
  }, [customVizRequest]);

  if (hasFailed && !isLoading) {
    return <ExplorePageFailure />;
  }

  if (isLoading || !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="full"
        w="full"
        flex="1"
      >
        <CustomVizCircularSpinner />
      </Box>
    );
  }

  return (
    <TableViewComponentPure
      data={data}
      isLoading={isLoading}
      chart={chart}
      setViewConfiguration={setViewConfiguration}
      viewConfiguration={viewConfiguration}
      selectedGroupBy={selectedGroupBy}
      selectedSecondaryBreakdown={selectedSecondaryBreakdown}
    />
  );
};
